import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, startOfWeek, endOfWeek } from "date-fns";
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import { cn } from "@/lib/utils";

interface CalendarViewProps {
  table: any;
}

export const CalendarView = ({ table }: CalendarViewProps) => {
  const [fields, setFields] = useState<any[]>([]);
  const [allFields, setAllFields] = useState<any[]>([]);
  const [records, setRecords] = useState<any[]>([]);
  const [dateField, setDateField] = useState<any>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (table) {
      fetchFields();
      fetchAllFields();
      fetchRecords();
    }
  }, [table]);

  const fetchFields = async () => {
    try {
      const { data, error } = await supabase
        .from("fields")
        .select("*")
        .eq("table_id", table.id)
        .eq("type", "date")
        .order("order_index", { ascending: true });

      if (error) throw error;
      if (data && data.length > 0) {
        setDateField(data[0]);
      }
      setFields(data || []);
    } catch (error: any) {
      toast.error("Failed to load fields");
    }
  };

  const fetchAllFields = async () => {
    try {
      const { data, error } = await supabase
        .from("fields")
        .select("*")
        .eq("table_id", table.id)
        .order("order_index", { ascending: true });

      if (error) throw error;
      setAllFields(data || []);
    } catch (error: any) {
      console.error("Failed to load all fields");
    }
  };

  const fetchRecords = async () => {
    try {
      const { data, error } = await supabase
        .from("records")
        .select("*")
        .eq("table_id", table.id);

      if (error) throw error;
      setRecords(data || []);
    } catch (error: any) {
      toast.error("Failed to load records");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!dateField) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="text-center max-w-md">
          <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Calendar className="h-8 w-8 text-primary" />
          </div>
          <h3 className="text-lg font-semibold mb-2">No date field found</h3>
          <p className="text-muted-foreground text-sm">
            Add a "Date" field to use Calendar view. Records will be displayed based on their date values.
          </p>
        </div>
      </div>
    );
  }

  const getRecordsByDate = (date: Date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return records.filter(r => {
      const recordDate = r.data?.[dateField.name];
      return recordDate && recordDate.startsWith(dateStr);
    });
  };

  const getDisplayField = () => {
    return allFields.find(f => f.name !== dateField.name) || allFields[0];
  };

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  return (
    <div className="h-full p-4 md:p-6 overflow-auto">
      {/* Calendar Header */}
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-xl md:text-2xl font-bold">
          {format(currentMonth, "MMMM yyyy")}
        </h2>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="icon"
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setCurrentMonth(new Date())}
          >
            Today
          </Button>
          <Button 
            variant="outline" 
            size="icon"
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="border border-border rounded-lg overflow-hidden">
        {/* Day Headers */}
        <div className="grid grid-cols-7 bg-muted/50">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <div key={day} className="text-center font-semibold text-xs text-muted-foreground py-3 border-b border-border">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7">
          {calendarDays.map((date, i) => {
            const dayRecords = getRecordsByDate(date);
            const displayField = getDisplayField();
            const isCurrentMonth = isSameMonth(date, currentMonth);
            const isCurrentDay = isToday(date);
            
            return (
              <div 
                key={i} 
                className={cn(
                  "min-h-[100px] md:min-h-[120px] border-b border-r border-border p-2 transition-colors",
                  !isCurrentMonth && "bg-muted/30",
                  isCurrentDay && "bg-primary/5"
                )}
              >
                <div className={cn(
                  "text-sm font-medium mb-2 h-7 w-7 flex items-center justify-center rounded-full",
                  isCurrentDay && "bg-primary text-primary-foreground",
                  !isCurrentMonth && "text-muted-foreground"
                )}>
                  {format(date, "d")}
                </div>
                <div className="space-y-1 overflow-hidden">
                  {dayRecords.slice(0, 3).map((record) => (
                    <div
                      key={record.id}
                      className="text-xs px-2 py-1 bg-primary/10 text-primary rounded truncate cursor-pointer hover:bg-primary/20 transition-colors"
                      title={record.data?.[displayField?.name] || "Untitled"}
                    >
                      {record.data?.[displayField?.name] || "Untitled"}
                    </div>
                  ))}
                  {dayRecords.length > 3 && (
                    <div className="text-xs text-muted-foreground px-2">
                      +{dayRecords.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
